import React, { useEffect, useRef, useState } from 'react';
import {
  fetchClassAnnouncements,
  addClassAnnouncement,
  uploadAnnouncementImage,
  deleteClassAnnouncement,
  ClassAnnouncement,
  Level
} from '../services/classAnnouncementService';
import { useAuth } from '../context/AuthContext';
import { formatTime, getRelativeDateLabel, isDifferentDay } from '../lib/dateUtils';
import DateSeparator from '../components/common/DateSeparator';

const LEVELS: Level[] = ['1', '2', '3', '4'];

const ClassAnnouncementsPage: React.FC = () => {
  const { user } = useAuth();
  const [showDenied, setShowDenied] = React.useState(user?.role !== 'admin');
  const [errorMsg] = React.useState('Access Denied: Only admins can access Class Announcements.');

  if (showDenied) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-lg flex flex-col items-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-700 mb-6">{errorMsg}</p>
          <button
            className="px-6 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
            onClick={() => setShowDenied(false)}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const [selectedLevel, setSelectedLevel] = useState<Level>('1');
  const [announcements, setAnnouncements] = useState<ClassAnnouncement[]>([]);
  const [pagingLastId, setPagingLastId] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [newMessage, setNewMessage] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [optionsOpenId, setOptionsOpenId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const announcementsContainerRef = useRef<HTMLDivElement | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState<boolean>(true);

  // Load newest announcements page for selected level
  const loadAnnouncements = async (level: Level) => {
    setLoading(true);
    try {
      // fetch newest page (ordered desc by createdAt)
      const res = await fetchClassAnnouncements(level, 20);
      const docs = res.documents || [];
      const fetched: ClassAnnouncement[] = (docs as any[]).map(doc => ({
        $id: doc.$id,
        message: doc.message || '',
        img: doc.img,
        time: doc.time || doc.$createdAt,
        level: doc.level as Level,
      }));

      // API gives newest->oldest, but UI expects oldest->newest ordering for scrolling to bottom
  const ordered = fetched.reverse();
  setAnnouncements(ordered);
  // we just loaded newest page, scroll to bottom to show latest
  setShouldScrollToBottom(true);
      setPagingLastId(res.lastId);
      setHasMore(res.hasMore);
    } catch (e) {
      // handle error
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    // reset paging and load newest page when level changes
    setAnnouncements([]);
    setPagingLastId(undefined);
    setHasMore(true);
    loadAnnouncements(selectedLevel);
  }, [selectedLevel]);

  useEffect(() => {
    if (shouldScrollToBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShouldScrollToBottom(false);
    }
  }, [announcements]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !imgUrl) return;
    let uploadedImgUrl = imgUrl;
    if (imageFile) {
      setUploading(true);
      try {
        uploadedImgUrl = await uploadAnnouncementImage(imageFile);
        setImgUrl(uploadedImgUrl);
      } catch (err) {
        // handle error
      }
      setUploading(false);
    }
    try {
      const created = await addClassAnnouncement(newMessage, selectedLevel, uploadedImgUrl || undefined);
      // prepend the newly created announcement so UI shows it immediately
      const newAnn: ClassAnnouncement = {
        $id: created.$id,
        message: newMessage,
        img: uploadedImgUrl || created.img,
        time: created.time || created.$createdAt,
        level: selectedLevel,
      };
      setAnnouncements((prev: ClassAnnouncement[]) => {
        // avoid duplicates
        if (prev.some(p => p.$id === newAnn.$id)) return prev;
        return [...prev, newAnn];
      });
  // after adding a new announcement, auto-scroll to show it
  setShouldScrollToBottom(true);
      setNewMessage('');
      setImgUrl('');
      setImageFile(null);
      setImagePreview(null);
    } catch (e) {
      // handle error
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImgUrl('');
  };

  // Fullscreen image modal
  const FullscreenImageModal = ({ src, onClose }: { src: string; onClose: () => void }) => (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white hover:text-gray-300"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={src}
          alt="Full preview"
          className="max-w-full max-h-[80vh] object-contain rounded shadow-lg"
        />
      </div>
    </div>
  );

  return (
    <div className="h-full flex bg-gray-100">
      {fullscreenImg && <FullscreenImageModal src={fullscreenImg} onClose={() => setFullscreenImg(null)} />}
      {/* Left sidebar - Levels */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Class Announcements</h2>
          <p className="text-sm text-gray-600 mb-3">Select a level to view/send announcements</p>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-200">
          {LEVELS.map(level => (
            <div
              key={level}
              onClick={() => setSelectedLevel(level)}
              className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedLevel === level ? 'bg-pink-50 border-r-2 border-blue-500' : ''}`}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                  {`L${level}`}
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="font-medium text-gray-900">Level {level}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Right side - Announcements area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <h3 className="font-medium text-gray-900">Level {selectedLevel} Announcements</h3>
        </div>
        {/* Announcements area */}
        <div
    ref={announcementsContainerRef}
    className="flex-1 overflow-y-auto p-4"
          style={{
            backgroundImage: `url('/assets/chatbg.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
            onScroll={async (e: React.UIEvent<HTMLDivElement>) => {
              // infinite scroll: when scrolled to top, load older announcements
              const target = e.currentTarget as HTMLDivElement;
              if (target.scrollTop <= 50 && hasMore && !loading) {
                const container = announcementsContainerRef.current;
                const prevScrollTop = container?.scrollTop ?? 0;
                const prevScrollHeight = container?.scrollHeight ?? 0;
                try {
                  setLoading(true);
                  // fetch older page using cursorAfter = pagingLastId
                  const res = await fetchClassAnnouncements(selectedLevel, 20, pagingLastId);
                  const docs = res.documents || [];
                  if (docs.length) {
                    const fetched: ClassAnnouncement[] = (docs as any[]).map(doc => ({
                      $id: doc.$id,
                      message: doc.message || '',
                      img: doc.img,
                      time: doc.time || doc.$createdAt,
                      level: doc.level as Level,
                    }));

                    // API returns newest->oldest; these are older than current pages, so we need to prepend
                    const ordered = fetched.reverse();
                    setAnnouncements((prev: ClassAnnouncement[]) => {
                      // filter out any duplicates from ordered
                      const orderedFiltered = ordered.filter(o => !prev.some(p => p.$id === o.$id));
                      const next = [...orderedFiltered, ...prev];
                      // after React updates DOM, restore scroll so the viewport stays at the same message
                      requestAnimationFrame(() => {
                        const newScrollHeight = container?.scrollHeight ?? 0;
                        if (container) {
                          container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
                        }
                      });
                      return next;
                    });
                    setPagingLastId(res.lastId);
                    setHasMore(res.hasMore);
                  } else {
                    setHasMore(false);
                  }
                } catch (err) {
                  console.error(err);
                } finally {
                  setLoading(false);
                }
              }
            }}
        >
          <div className="flex flex-col gap-2 justify-end">
            {loading ? (
              <div className="text-center text-gray-500">Loading announcements...</div>
            ) : announcements.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                No announcements yet for this level.
              </div>
            ) : (
              announcements.map((msg, index) => {
                const previousMsg = index > 0 ? announcements[index - 1] : null;
                const showDateSeparator = !previousMsg || isDifferentDay(msg.time, previousMsg.time);

                return (
                  <React.Fragment key={msg.$id}>
                    {showDateSeparator && (
                      <DateSeparator label={getRelativeDateLabel(msg.time)} />
                    )}
                    <div className="flex justify-end">
                      <div className="group relative">
                        <div
                          className="max-w-xs rounded-lg px-4 py-2 shadow-md relative chat-bubble flex flex-col items-center bg-white"
                          style={{ padding: msg.img ? '12px' : undefined }}
                        >
                          {/* Three-dot options icon */}
                          <div
                            className="absolute bottom-1 left-[10px] z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={() => setOptionsOpenId(optionsOpenId === msg.$id ? null : msg.$id)}
                          >
                            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <circle cx="5" cy="12" r="2" fill="#555" />
                              <circle cx="12" cy="12" r="2" fill="#555" />
                              <circle cx="19" cy="12" r="2" fill="#555" />
                            </svg>
                          </div>
                          {/* Options menu */}
                          {optionsOpenId === msg.$id && (
                            <div className="absolute bottom-7 left-[10px] bg-white border rounded shadow z-20 min-w-[80px]">
                              <button
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600"
                                onClick={async () => {
                                  await deleteClassAnnouncement(msg.$id);
                                  setOptionsOpenId(null);
                                  loadAnnouncements(selectedLevel);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                          {/* Image and message content */}
                          {msg.img && (
                            <img
                              src={msg.img}
                              alt="attachment"
                              className="block rounded-lg object-contain cursor-pointer mb-2"
                              style={{ maxWidth: 320, maxHeight: 180, width: '100%' }}
                              onClick={() => setFullscreenImg(msg.img!)}
                            />
                          )}
                          {msg.message && (
                            <div className="text-sm whitespace-pre-line w-full text-left">{msg.message}</div>
                          )}
                          <div className="text-xs text-gray-500 text-right mt-1 w-full">
                            {formatTime(msg.time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
        {/* Message input */}
        <form
          onSubmit={handleSend}
          className="bg-white border-t border-gray-200 p-4"
        >
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type your announcement..."
              className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label className="cursor-pointer flex items-center">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              <span className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-full">📎</span>
            </label>
            {imagePreview && (
              <div className="relative">
                <img src={imagePreview} alt="preview" className="max-h-12 max-w-[72px] rounded shadow object-contain" style={{ aspectRatio: '4/3', background: '#eee' }} />
                <button type="button" onClick={removeImage} className="absolute top-0 right-0 bg-red-500 text-white rounded-full px-1 py-0.5 text-xs">✕</button>
              </div>
            )}
            <button
              type="submit"
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-semibold shadow disabled:opacity-50"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassAnnouncementsPage; 